import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Autocomplete, Grid, Paper, Typography, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import TableN from "../../reusableComponents/components/generics/TableN";
import { fetchHealthFacilitiesWithProductBundle } from "../../action";
import Delete from "@mui/icons-material/Delete";
import _ from "lodash";
import CommonSnackbar from "../../reusableComponents/components/generics/CommonSnackbar";

const StyledPaper = styled(Paper)(({ theme }) => ({
  ...theme.paper.paper,
}));

const StyledPaperHeader = styled(Grid)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

function ClaimItemsPannel({ onDataChange, claimData, disabled, nonRemovableProductIds, preAuthData }) {
  const SignInSuccess = useSelector((state) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([
    {
      productId: "",
      item: "",
      quantity: "",
      price: "",
      tmValue: "",
      countedPrice: "",
      explanation: "",
      priceAsked: "",
    },
  ]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [totalClaimed, setTotalClaimed] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const healthData = SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees?.[0];
  const healthFacilityUuid = claimData?.healthFacility?.uuid || healthData?.uuid;
  const healthSupport = SignInSuccess?.fosaReducer?.healthFacilityHealthSupport ?? 100;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  /* eslint-disable no-unused-vars -- kept for Dialog/handleClose consistency with ClaimActsPannel */
  const [popupMessage, setPopupMessage] = useState("");
  const [deleteType, setDeleteType] = useState(false);
  const [index, setIndex] = useState("");
  /* eslint-enable no-unused-vars */
  const [prevRows, setPrevRows] = useState([]);

  useEffect(() => {
    if (healthFacilityUuid) {
      dispatch(fetchHealthFacilitiesWithProductBundle(healthFacilityUuid, ["first:50"]));
    }
  }, [dispatch, healthFacilityUuid]);

  useEffect(() => {
    if (SignInSuccess?.fosaReducer?.healthFacilitiesProductBundleData) {
      const products = SignInSuccess.fosaReducer.healthFacilitiesProductBundleData;
      setFilteredServices(products || []);
    }
  }, [SignInSuccess?.fosaReducer?.healthFacilitiesProductBundleData]);

  useEffect(() => {
    // Prefer health products from fetchClaimById (claimProducts); fall back to legacy items
    const healthProducts = claimData?.claimProducts ?? [];
    const legacyItems = claimData?.items ?? [];
    const source = healthProducts.length > 0 ? healthProducts : legacyItems;
    const isClaimProducts = healthProducts.length > 0;

    if (source.length > 0) {
      const initialRows = source.map((itemRow) => {
        const jsonExt = JSON.parse(itemRow.jsonExt || itemRow.json_ext || "{}");
        const product = isClaimProducts ? itemRow.healthProduct : itemRow.item;
        const fullCode = product?.fullCode || product?.full_code || "";
        // const displayName = fullCode || product?.name || product?.label || "";
        const productId = product?.uuid ?? product?.id;
        
        const price = itemRow.priceAsked ?? product?.priceHomologated ?? product?.price_homologated;
        return {
          productId: productId != null ? String(productId) : "",
          // item: displayName,
          item: fullCode,
          price,
          quantity: itemRow.qtyProvided,
          priceAsked: itemRow.priceAsked,
          countedPrice: jsonExt.countedPrice,
          tmValue: jsonExt.tmValue,
          explanation: itemRow.explanation,
        };
      });
      setRows(initialRows);
      setPrevRows(initialRows);
      setSelectedServices(initialRows.map((row) => row.productId).filter(Boolean));
      calculateTotalClaimed(initialRows);
      onDataChange(initialRows);
    } else {
      setRows([
        {
          productId: "",
          item: "",
          quantity: "",
          price: "",
          tmValue: "",
          countedPrice: "",
          explanation: "",
          priceAsked: "",
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDataChange from parent; avoid resetting rows on re-render
  }, [claimData]);

  const handleServiceChange = (index, option) => {
    if (!option || option.id == null || option.id === "") return;
    setPrevRows(rows);

    if (option?.productM2m?.apStatus === 'REQUIS' && !preAuthData?.paProducts) {
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
      setSnackbarMessage("ce produit nécessite un accord préalable");
      return;
    }

    const optionIdStr = String(option.id);
    const selectedProduct = filteredServices.find(
      (item) => getProductId(item) != null && String(getProductId(item)) === optionIdStr
    );
    if (!selectedProduct?.productM2m) return;

    const productM2m = selectedProduct.productM2m;
    const price = Number(selectedProduct.agreedPrice ?? productM2m.priceHomologated ?? 0);
    const healthSupportPct = Number(healthSupport) || 100;
    const countedPricePerUnit = _.round(price * (healthSupportPct / 100), 2);
    const tmValuePerUnit = _.round(price * ((100 - healthSupportPct) / 100), 2);

    const fullCode = productM2m.fullCode || productM2m.full_code || "";
    const displayLabel = fullCode || productM2m.label || "";
    const productIdStr = getProductId(selectedProduct) ?? "";

    const newRows = rows.map((row, i) =>
      i === index
        ? {
          ...row,
          productId: productIdStr,
          item: displayLabel,
          price,
          quantity: 1,
          priceAsked: price,
          countedPrice: String(countedPricePerUnit),
          tmValue: String(tmValuePerUnit),
        }
        : row
    );
    setRows(newRows);
    onDataChange(newRows);
    const newSelectedServices = newRows
      .map((row) => row.productId)
      .filter((s) => s !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
  };

  const handleChange = (index, event) => {
    setPrevRows(rows);
    const { name, value } = event.target;
    const newRows = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setRows(newRows);
    onDataChange(newRows);
    if (name === "quantity") {
      calculateTotalClaimed(newRows);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        productId: "",
        item: "",
        quantity: "",
        price: "",
        tmValue: "",
        countedPrice: "",
        explanation: "",
        priceAsked: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    const newSelectedServices = newRows
      .map((row) => row.productId)
      .filter((id) => id !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
    onDataChange(newRows);
  };

  // Use productM2m.id (bundle) or uuid for consistent selection; normalize to string for comparison
  const getProductId = (item) => {
    const id = item?.productM2m?.uuid ?? item?.productM2m?.id;
    return id != null ? String(id) : undefined;
  };

  const getProductOption = (item) => {
    const productM2m = item?.productM2m;
    const id = getProductId(item);
    const fullCode = productM2m?.fullCode || productM2m?.full_code || "";
    const label = fullCode || productM2m?.label || "";
    return {
      ...item,
      label,
      id: id ?? "",
    };
  };

  const getAvailableProducts = (currentProductId) => {
    const currentId = currentProductId != null ? String(currentProductId) : "";
    return filteredServices.filter((item) => {
      const id = getProductId(item);
      if (id == null || id === "") return false;
      return (
        !selectedServices.some((s) => String(s) === id) ||
        id === currentId
      );
    });
  };

  const calculateTotalClaimed = (rows) => {
    const total = rows.reduce((sum, row) => sum + row.quantity * row.price, 0);
    setTotalClaimed(total);
  };

  const totalTmValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const tmValue = r.tmValue ? r.tmValue : null;
      return sum + (tmValue ? parseFloat(tmValue) * quantity : 0);
    }, 0),
    2
  );
  const totalCounterPriceValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const countedPrice = r.countedPrice ? r.countedPrice : null;
      return sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0);
    }, 0),
    2
  );

  const headers = () => [
    t("Items"),
    t("Quantity"),
    t("Price"),
    t("Ticket"),
    t("CountedPrice"),
    t("Explanation"),
    t("Action"),
  ];

  const multiplyVal = (value, multiplyBy) => {
    return parseFloat(value) * parseFloat(multiplyBy);
  };

  const itemFormatters = () => [
    (row, index) => {
      const availableOptions = getAvailableProducts(row.productId)
        .map(getProductOption);
      const rowProductIdStr = row.productId != null ? String(row.productId) : "";
      const selectedOption = rowProductIdStr
        ? (availableOptions.find((opt) => String(opt.id) === rowProductIdStr) ?? { id: row.productId, label: row.item || "" })
        : null;
      return (
        <Autocomplete
          options={availableOptions}
          getOptionLabel={(opt) => opt?.label ?? ""}
          isOptionEqualToValue={(opt, val) => (opt?.id != null && val?.id != null ? String(opt.id) === String(val.id) : opt === val)}
          sx={{ width: 300 }}
          size="small"
          value={selectedOption}
          disabled={disabled}
          onChange={(e, option) => {
            handleServiceChange(index, option);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      );
    },
    (row, index) => (
      <TextField
        type="number"
        className="no-arrow"
        name="quantity"
        size="small"
        value={row.quantity ? row.quantity : ""}
        onChange={(e) => handleChange(index, e)}
        disabled={disabled}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="price"
        value={row.price != null && row.price !== "" ? _.round(Number(row.price) * (Number(row.quantity) || 1), 2) : ""}
        size="small"
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="ticket"
        size="small"
        value={row.tmValue ? multiplyVal(row.tmValue, row.quantity || 1) : ""}
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="countedPrice"
        size="small"
        value={row.countedPrice ? multiplyVal(row.countedPrice, row.quantity || 1) : ""}
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="explanation"
        size="small"
        disabled={true}
        value={row.explanation ? row.explanation : ""}
      />
    ),
    (row, index) => {
      const isFromPreauth = nonRemovableProductIds?.size > 0 && row.productId && nonRemovableProductIds.has(String(row.productId));

      return disabled || isFromPreauth ? (<div></div>) : (
        <Button
          startIcon={<Delete style={{ color: "red" }} />}
          onClick={() => deleteRow(index)}
        />
      );
    },
  ];
  const preHeaders = [
    "\u200b",
    "",
    totalClaimed > 0 ? t("Total Claimed: ") + totalClaimed : "",
    totalTmValue > 0 ? t("TotalTm") + totalTmValue : "",
    totalCounterPriceValue > 0
      ? t("TotalCounted") + totalCounterPriceValue
      : "",
    " ",
    " ",
  ];

  const handleClose = () => {
    setIsPopupOpen(false);
    if (deleteType === 'delete') {
      deleteRow(index);
    } else {
      // Revert to previous rows if popup is triggered and deleteType is not 'delete'
      setRows(prevRows);
      onDataChange(prevRows);
      calculateTotalClaimed(prevRows);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarSeverity("");
    setSnackbarOpen(false);
  };

  return (
    <StyledPaper>
      <StyledPaperHeader container alignItems="center" direction="row">
        <Grid item alignItems="center">
          <Typography variant="h6">{t("ClaimItemsServices")}</Typography>
        </Grid>
      </StyledPaperHeader>
      <TableN
        headers={headers()}
        itemFormatters={itemFormatters()}
        preHeaders={preHeaders}
        items={rows}
        withSelection={"single"}
        withPagination={false}
      />
      {!disabled && <Button onClick={addRow}>{t("Add")}</Button>}
      <Dialog open={isPopupOpen} onClose={handleClose}>
        <DialogTitle>{t("Alert")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(popupMessage)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("OK")}
          </Button>
        </DialogActions>
      </Dialog>

      <CommonSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />

    </StyledPaper>
  );
}

export default ClaimItemsPannel;
